import React, { useEffect, useMemo } from 'react';
import moment from 'moment';

import { PropTypes } from 'prop-types';

import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import paginationFactory from 'react-bootstrap-table2-paginator';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css';

import {
   RemoveBottomMargin,
   TwoSideFlexContainer,
   Description,
   SimpleFlexRow,
   SimpleFlexItem,
   DescriptionSummary,
} from '../../../../styledComponents/styledComponents';
import { SIMPLE_DATE_FORMAT, SIMPLE_TIME_FORMAT } from '../../../../constants/general';
import { formatTimeNumberInt } from '../../../../util/NumberFormatter';

import storePropTypes from '../../../../redux/store/propTypes';
import BlockingIndicator from '../../common/BlockingIndicator';
import { CsvExportFromApiButton } from '../../../common';
import { useState } from 'react';
import IpRowModal from './IpRowModal';
import useBlockListFetcher from '../../../../hooks/useBlockListFetcher';
import { IP_BLOCKLIST } from '../../../../constants/general';

import { Tooltip as ReactTooltip } from 'react-tooltip/dist/react-tooltip.umd';

import { JwtAudienceOptions } from '../../../../constants/AccessLevels';
import * as browserStorage from '../../../../misc/BrowserStorageManager';
import IpMetricsHelp from './IpTableDescription';

const { SearchBar } = Search;

const getColumns = (concurrencyIsDesired, hasGuardianAccess) => {
   let { isAdmin } = browserStorage.getUserInfo();
   const columns = [
      {
         text: 'IP',
         dataField: 'ip',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         classes: 'ip-cell',
      },
      ...(isAdmin
         ? [
              {
                 text: 'Orga',
                 dataField: 'orgaHash',
                 sort: true,
                 style: { verticalAlign: 'middle', textAlign: 'right' },
                 headerAlign: 'right',
                 searchable: false,
                 headerStyle: {
                    whiteSpace: 'normal',
                    maxWidth: '70px',
                 },
              },
           ]
         : []),
      {
         text: 'Duration/Sum',
         dataField: 'durationSum',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         searchable: false,
         formatter: formatTimeNumberInt,
      },
      {
         text: 'Duration/Avg',
         dataField: 'durationAvg',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         searchable: false,
         formatter: formatTimeNumberInt,
      },
      {
         text: 'Sessions',
         dataField: 'sessionCount',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right', maxWidth: '60px' },
         searchable: false,
         headerAlign: 'right',
         headerStyle: {
            whiteSpace: 'normal',
            maxWidth: '70px',
         },
      },
      {
         text: 'Streams',
         dataField: 'streamCount',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         searchable: false,
         headerAlign: 'right',
         headerStyle: {
            whiteSpace: 'normal',
            maxWidth: '70px',
         },
      },
      {
         text: 'Tokens',
         dataField: 'tokenCount',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         searchable: false,
         headerAlign: 'right',
         headerStyle: {
            whiteSpace: 'normal',
            maxWidth: '70px',
         },
      },
      {
         text: 'Top Providers',
         dataField: 'topProviders',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         formatter: (values) => (values.length === 0 ? 'N/A' : values.join(', ')),
      },
      {
         text: 'Top Countries',
         dataField: 'topCountries',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         formatter: (values) => (values.length === 0 ? 'N/A' : values.join(', ')),
      },
      {
         text: 'Top Referrers',
         dataField: 'topReferrers',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         formatter: (values) => (values.length === 0 ? 'N/A' : values.join(', ')),
      },
      {
         text: 'Top Agents',
         dataField: 'topAgents',
         hidden: true,
      },
      {
         text: 'First stream start',
         dataField: 'streamStart',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         searchable: false,
         formatter: (value) => moment.utc(value).format(`${SIMPLE_DATE_FORMAT} ${SIMPLE_TIME_FORMAT}`),
      },
      {
         text: 'Last stream end',
         dataField: 'streamEnd',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         searchable: false,
         formatter: (value) => moment.utc(value).format(`${SIMPLE_DATE_FORMAT} ${SIMPLE_TIME_FORMAT}`),
      },
      {
         text: 'Max Concurrency',
         dataField: 'concurrency',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         searchable: false,
         hidden: concurrencyIsDesired ? false : true,
      },
   ];

   if (hasGuardianAccess) {
      columns.push({
         text: 'blocked',
         dataField: 'blocked',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'center' },
         formatter: (cell) => (cell === 'N/A' ? 'N/A' : <BlockingIndicator isBlocked={cell === true} />),
         headerAlign: 'center',
         headerStyle: { width: '80px' },
         searchable: false,
      });
   }

   columns.push({
      text: '',
      dataField: '__details',
      isDummyField: true,
      searchable: false,
      headerStyle: {
         width: '36px',
         minWidth: '36px',
      },
      style: {
         textAlign: 'center',
         verticalAlign: 'middle',
         padding: '0',
      },
      formatter: () => (
         <>
            <div className='row-action' data-tooltip-id={'openDetails'} data-tooltip-html={'Get more details'}>
               <span className='row-action-icon'>
                  <i id='detailMarker' className='bi bi-chevron-right text-blue'></i>
               </span>
            </div>
            <ReactTooltip
               id='openDetails'
               place={'top'}
               className={'reduced-line-height bring-on-top'}
               backgroundColor={'rgba(0,0,0,0.9)'}
               textColor={'rgb(240,240,240)'}
               style={{ textAlign: 'center' }}
            />
         </>
      ),
   });

   return columns;
};

function IpTable({ data, concurrencyIsDesired, url, httpHeader, cancelToken, requestBody }) {
   const [modal, setModal] = useState({
      toggleShow: false,
      rowData: {},
   });

   const rowEvents = {
      onClick: (e, rowData) => {
         setModal({ toggleShow: true, rowData });
      },
      onKeyDown: (e, rowData) => {
         if (e.key === 'Enter') {
            setModal({ toggleShow: true, rowData });
         }
      },
   };

   const { toggleShow, rowData } = modal;

   const userInfo = browserStorage.getUserInfo();
   const hasGuardianAccess = userInfo.aud.includes(JwtAudienceOptions.GUARDIAN);

   const { fetchBlockList, blockedMap } = useBlockListFetcher({ type: IP_BLOCKLIST });

   const columns = useMemo(() => getColumns(concurrencyIsDesired, hasGuardianAccess), [
      concurrencyIsDesired,
      hasGuardianAccess,
   ]);

   useEffect(() => {
      if (hasGuardianAccess) fetchBlockList();
   }, [fetchBlockList, hasGuardianAccess]);

   const tableData = useMemo(() => {
      if (!hasGuardianAccess || !blockedMap) return data;
      return data.map((row) => ({ ...row, blocked: blockedMap[row.ip] === true }));
   }, [data, blockedMap, hasGuardianAccess]);

   return (
      <>
         {data.length > 0 && (
            <>
               <details style={{ marginBottom: '10px' }}>
                  <DescriptionSummary>Table columns descriptions</DescriptionSummary>
                  <IpMetricsHelp />
               </details>
               <RemoveBottomMargin>
                  <ToolkitProvider keyField='ip' data={tableData} columns={columns} bootstrap4 search>
                     {(props) => (
                        <>
                           <TwoSideFlexContainer $marginTop={'20px'}>
                              <Description alpha={0.5} fontSizePX={14} marginTopPx={6} marginBottomPx={15}>
                                 Click on a table row to view a breakdown of the respective IP, including the option to
                                 block it.
                              </Description>
                              <SimpleFlexRow>
                                 <SearchBar {...props.searchProps} tableId={'token'} />
                                 <SimpleFlexItem $marginLeftInPx={8} $bottomMarginPx={10}>
                                    <CsvExportFromApiButton
                                       url={url}
                                       header={httpHeader}
                                       cancelToken={cancelToken}
                                       filename={'IP_Details'}
                                       inputStyle
                                       heightInPx={34}
                                       method={'post'}
                                       body={requestBody}
                                    />
                                 </SimpleFlexItem>
                              </SimpleFlexRow>
                           </TwoSideFlexContainer>
                           <BootstrapTable
                              {...props.baseProps}
                              id='misuseTable'
                              pagination={paginationFactory({
                                 sizePerPage: 10,
                                 hideSizePerPage: true,
                              })}
                              striped
                              hover
                              rowEvents={rowEvents}
                              condensed={true}
                              classes='table-responsive-xl'
                           />
                        </>
                     )}
                  </ToolkitProvider>
               </RemoveBottomMargin>
               {toggleShow && (
                  <IpRowModal
                     rowData={rowData}
                     closeModal={() => setModal({ ...modal, toggleShow: false })}
                     onBlockAction={fetchBlockList}
                  />
               )}
            </>
         )}
      </>
   );
}

IpTable.propTypes = {
   data: PropTypes.arrayOf(storePropTypes.ipMisuseResultRow),
   concurrencyIsDesired: PropTypes.bool,
   url: PropTypes.string,
   httpHeader: PropTypes.object,
   cancelToken: PropTypes.object,
   requestBody: PropTypes.object,
};

export default IpTable;
